// src/hooks/useBuyerTemplateData.js
import { useEffect, useRef, useState } from "react";
import { getUserTemplate } from "../services/refinanceNetService";

export const useBuyerTemplateData = (mainId, userId, companyId) => {
  const [templateData, setTemplateData] = useState(null);
  const lastCompanyIdRef = useRef(null);


  useEffect(() => {
    if (!mainId || !userId || !companyId) return;
    if (lastCompanyIdRef.current === companyId) return;
    lastCompanyIdRef.current = companyId;

    const safeParse = (data) => {
      if (!data) return null;
      if (Array.isArray(data) || typeof data === "object") return data;
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    };

    const toCamel = (s = "") =>
      s
        .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
        .replace(/^([A-Z])/, (m) => m.toLowerCase());

    const parseValue = (item) => {
      if (!item) return "0.00";
      const v = item.value ?? item.rate ?? item.Cash ?? item.total ?? null;
      if (v == null) return "0.00";
      if (typeof v === "string") {
        const s = v.replace(/,/g, "").trim();
        return s === "" ? "0.00" : s;
      }
      return String(v);
    };

    const parseArraySection = (arr) => {
      const parsed = [];
      const data = safeParse(arr);
      if (!Array.isArray(data)) return parsed;
      for (const it of data) {
        if (!it?.key) continue;
        parsed.push({
          ...it,
          camelKey: toCamel(it.key),
          value: parseValue(it),
          total: parseValue(it),
        });
      }
      return parsed;
    };

    const fetchTemplate = async () => {

      try {
        const response = await getUserTemplate(companyId);
        const data = response;
        if (!data) return;

        const cash_section = [
          {
            key: "origination_factor",
            camelKey: "originationFactor",
            label: "Origination Factor",
            unit: "%",
            value: "0.00",
          },
          {
            key: "document_preparation",
            camelKey: "documentPreparation",
            label: "Document Preparation",
            unit: "$",
            value: "0.00",
          },
          {
            key: "tax_service_contract",
            camelKey: "taxServiceContract",
            label: "Tax Service Contract",
            unit: "$",
            value: "0.00",
          },
          {
            key: "underwriting",
            camelKey: "underwriting",
            label: "Underwriting",
            unit: "$",
            value: "0.00",
          },
          {
            key: "processing_fee",
            camelKey: "processingFee",
            label: "Processing Fee",
            unit: "$",
            value: "0.00",
          },
          {
            key: "appraisal_fee",
            camelKey: "appraisalFee",
            label: "Appraisal Fee",
            unit: "$",
            value: "0.00",
          },
        ];

        const buyerSections = {
          tenure: parseArraySection(data.buyer_tenure_section),
          insurance: parseArraySection(data.buyer_taxes_insurance_section),
          prepaid: parseArraySection(data.buyer_prepaid_section),
          payments: parseArraySection(data.buyer_payments_section),
          closingCosts: parseArraySection(data.refinance_closing_costs_section2),
          fha: parseArraySection(data.fha_closing_costs),
          va: parseArraySection(data.va_closing_costs),
          usda: parseArraySection(data.usda_closing_costs),
          convAdj: parseArraySection(data.conventional_adjustable_section),
          conventional: parseArraySection(data.refinance_closing_costs_section1),
          cash: parseArraySection(cash_section),
        };

        const creditReport = buyerSections.payments.find((it) => it.key === "credit_report");
        
        if (creditReport) {
          ["conventional", "fha", "va", "usda", "cash"].forEach((type) => {
            buyerSections[type] = [...buyerSections[type], creditReport];
          });
        }

        buyerSections.conventional = buyerSections.conventional.filter(
          (it) => it.key !== "points_pct",
        );
        buyerSections.conventional = buyerSections.conventional.filter(
          (it) => it.key !== "flood_certification",
        );

        setTemplateData({ buyerSections });
      } catch (err) {
        console.error("Error loading buyer template:", err);
        lastCompanyIdRef.current = null;
      }
    };

    fetchTemplate();
  }, [mainId, userId, companyId]);

  return templateData;
};
